import React from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotate,
  faCircleXmark,
  faFilterCircleXmark,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import AlertAccordionSection from "./AlertAccordionSection";
import AlertCriticalSection from "./AlertCriticalSection";
import InactiveTerminalsSection from "./InactiveTerminalsSection";

const PRIMARY_KPI_LABELS = [
  "Total alertas criticas",
  "Gestiones pendientes",
  "Proximos contactos vencidos",
  "Comercios con falta de facturacion",
];

const SECONDARY_KPI_ORDER = [
  "Seguimientos activos",
  "Bajo consumo sostenido",
  "Necesitan rollos",
];

const METRIC_CARD_CLASS_BY_LABEL = {
  "Total alertas criticas": "crm-alert-metric-card--critical-total",
  "Gestiones pendientes": "crm-alert-metric-card--pending",
  "Proximos contactos vencidos": "crm-alert-metric-card--overdue",
  "Comercios con falta de facturacion": "crm-alert-metric-card--billing-gap",
};

const hasActiveFilters = (filters = {}, searchDraft = "") =>
  Boolean(
    filters.fecha ||
    filters.prioridad ||
    filters.estado ||
    filters.provincia ||
    filters.responsable ||
    filters.tipo ||
    String(searchDraft || "").trim()
  );

export default function LegacyAlertCenter(props) {
  const {
    darkMode,
    cargas,
    errores,
    alertas,
    alertasFiltros,
    setAlertasFiltros,
    alertasSearchDraft,
    setAlertasSearchDraft,
    handleAlertasSearchKeyDown,
    aplicarFiltrosAlertas,
    resetAlertasFilters,
    alertaHeaderClockLabel,
    alertasCriticasExpandido,
    setAlertasCriticasExpandido,
    copyToClipboard,
    alertasFacturacionFiltradas,
    alertasFacturacionSearch,
    setAlertasFacturacionSearch,
    alertasTerminalExtraviadaFiltradas,
    alertasTerminalExtraviadaSearch,
    setAlertasTerminalExtraviadaSearch,
    alertaCardColapsadas,
    toggleAlertCard,
    setAlertaAccionItem,
    currency,
    percent,
    PostventaBadge,
    toneForPriority,
    toneForGestionState,
    toneForSegment,
    toneForPattern,
    renderZocoCheckPanel,
    inactivos,
    cargasInactivos,
    erroresInactivos,
    inactivosFiltrados,
    inactivosSearchDraft,
    setInactivosSearchDraft,
    inactivosFiltros,
    setInactivosFiltros,
    inactivosProvinciaOptions,
    aplicarFiltrosInactivos,
    inactivoCardColapsadas,
    toggleInactivoCard,
    ejecutarAccionAlerta,
    abrirGestionInactiva,
  } = props;

  const data = alertas || {
    fechaReferencia: alertasFiltros?.fecha || "",
    kpis: [],
    estadosGestion: ["Pendiente", "En curso", "Contactado", "Pospuesto", "Cerrado"],
    alertasCriticas: [],
    alertasFacturacion: [],
    alertasZocoCheck: [],
    alertasTerminalExtraviada: [],
    filas: [],
  };

  const kpiByLabel = new Map((data.kpis || []).map((item) => [item.label, item]));
  const primaryKpis = PRIMARY_KPI_LABELS.map((label) => kpiByLabel.get(label)).filter(Boolean);
  const secondaryKpis = SECONDARY_KPI_ORDER.map((label) => kpiByLabel.get(label)).filter(Boolean);
  const activeFilters = hasActiveFilters(alertasFiltros, alertasSearchDraft);
  const displayDate = (() => {
    const raw = String(data.fechaReferencia || alertasFiltros.fecha || "").trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [year, month, day] = raw.split("-");
      return `${day}/${month}/${year}`;
    }

    return raw || "-";
  })();
  const primaryMetrics = primaryKpis.length
    ? primaryKpis
    : PRIMARY_KPI_LABELS.map((label) => ({
        label,
        value: 0,
        description: "Sin datos para la fecha y filtros seleccionados.",
      }));
  const compactMetrics = secondaryKpis.length
    ? secondaryKpis
    : SECONDARY_KPI_ORDER.map((label) => ({ label, value: 0, tone: "default" }));

  return (
    <div className="crm-alert-center">
      <section className={`crm-alert-figma-board ${darkMode ? "is-dark" : ""}`}>
        <div className="crm-alert-header-row">
          <div className="crm-alert-title-block">
            <span className="crm-alert-kicker">CRM POSTVENTAS</span>
            <h2>Centro de alertas</h2>
          </div>
          <div className="crm-alert-pills-row">
            <span>Facturación {data.alertasFacturacion?.length || 0}</span>
            <span>Críticas {data.alertasCriticas?.length || 0}</span>
            <span>Operativas manuales {(data.filas || []).filter((item) => !item.esAutomatica).length}</span>
            <span className="crm-alert-pills-strong">{displayDate} | {String(alertaHeaderClockLabel || "").split("|")[1]?.trim() || "-"}</span>
          </div>
        </div>

        <div className="crm-alert-toolbar-row">
          <Form.Control
            type="date"
            className="crm-postventa-filter-control crm-alert-toolbar-date"
            value={alertasFiltros.fecha}
            onChange={(event) => setAlertasFiltros((prev) => ({ ...prev, fecha: event.target.value }))}
            aria-label="Filtrar alertas por fecha"
          />
          <Form.Select
            className="crm-postventa-filter-control crm-alert-toolbar-select"
            value={alertasFiltros.prioridad}
            onChange={(event) => setAlertasFiltros((prev) => ({ ...prev, prioridad: event.target.value }))}
            aria-label="Filtrar alertas por prioridad"
          >
            <option value="">Prioridad</option>
            <option value="Crítica">Crítica</option>
            <option value="Alta">Alta</option>
            <option value="Media">Media</option>
            <option value="Baja">Baja</option>
          </Form.Select>
          <Form.Select
            className="crm-postventa-filter-control crm-alert-toolbar-select"
            value={alertasFiltros.estado}
            onChange={(event) => setAlertasFiltros((prev) => ({ ...prev, estado: event.target.value }))}
            aria-label="Filtrar alertas por estado"
          >
            <option value="">Estados</option>
            {(data.estadosGestion || []).map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </Form.Select>
          <div className="crm-alert-search-wrap crm-alert-toolbar-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <Form.Control
              type="search"
              className="crm-postventa-filter-control"
              placeholder="Buscar por comercio, CUIT, provincia o responsable..."
              value={alertasSearchDraft}
              onChange={(event) => setAlertasSearchDraft(event.target.value)}
              onKeyDown={handleAlertasSearchKeyDown}
              aria-label="Buscar alertas"
            />
            {String(alertasSearchDraft || "").trim() ? (
              <button type="button" className="crm-alert-search-clear" onClick={() => setAlertasSearchDraft("")} aria-label="Limpiar búsqueda">
                <FontAwesomeIcon icon={faCircleXmark} />
              </button>
            ) : null}
          </div>
          <button type="button" className="crm-alert-refresh-icon" onClick={aplicarFiltrosAlertas} aria-label="Actualizar alertas">
            <FontAwesomeIcon icon={faArrowsRotate} />
          </button>
        </div>

        <div className="crm-alert-metrics-shell">
          <div className="crm-alert-metrics-grid">
            {primaryMetrics.map((item) => (
              <article
                className={`crm-alert-metric-card ${METRIC_CARD_CLASS_BY_LABEL[item.label] || ""} ${darkMode ? "is-dark" : ""}`}
                key={item.label}
              >
                <button type="button" className="crm-alert-help-dot" aria-label={`Información de ${item.label}`}>?</button>
                <strong>{item.value}</strong>
                <span>{item.label}</span>
              </article>
            ))}
          </div>
          <div className="crm-alert-compact-metrics">
            {compactMetrics.map((item) => (
              <article className={`crm-alert-compact-card tone-${item.tone || "default"} ${darkMode ? "is-dark" : ""}`} key={item.label}>
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </article>
            ))}
          </div>
        </div>

        {errores ? (
          <section className={`crm-postventa-panel crm-alert-surface ${darkMode ? "is-dark" : ""}`}>
            <Alert variant="danger" className="mb-0">{errores}</Alert>
          </section>
        ) : (
          <AlertCriticalSection
            darkMode={darkMode}
            rows={data.alertasCriticas || []}
            expanded={alertasCriticasExpandido}
            onToggleExpanded={() => setAlertasCriticasExpandido((prev) => !prev)}
            onCopyMessage={copyToClipboard}
            PostventaBadge={PostventaBadge}
            toneForPriority={toneForPriority}
            toneForGestionState={toneForGestionState}
            toneForSegment={toneForSegment}
            toneForPattern={toneForPattern}
          />
        )}
      </section>

      {!cargas && !errores && !alertas ? (
        <section className={`crm-postventa-panel crm-alert-surface ${darkMode ? "is-dark" : ""}`}>
          <div className="crm-alert-empty-block">
            <h3>Sin alertas cargadas</h3>
            <p>No hay datos disponibles para mostrar todavía. Podés actualizar la información o cambiar la fecha y los filtros.</p>
            <Button variant="success" onClick={aplicarFiltrosAlertas}>
              <FontAwesomeIcon className="me-2" icon={faArrowsRotate} />
              Reintentar carga
            </Button>
          </div>
        </section>
      ) : null}

      <AlertAccordionSection
        darkMode={darkMode}
        title="Alertas de facturación"
        total={data.alertasFacturacion?.length || 0}
        rows={alertasFacturacionFiltradas}
        emptyMessage="No hay alertas de facturación que coincidan con los filtros actuales."
        searchValue={alertasFacturacionSearch}
        onSearchChange={setAlertasFacturacionSearch}
        placeholder="Buscar por comercio, CUIT, terminal, responsable, teléfono..."
        collapsedSet={alertaCardColapsadas}
        onToggleCollapse={toggleAlertCard}
        onActionClick={setAlertaAccionItem}
        onCopyMessage={copyToClipboard}
        currency={currency}
        percent={percent}
        PostventaBadge={PostventaBadge}
        toneForPriority={toneForPriority}
        toneForGestionState={toneForGestionState}
        toneForSegment={toneForSegment}
        toneForPattern={toneForPattern}
      />

      {renderZocoCheckPanel()}

      <AlertAccordionSection
        darkMode={darkMode}
        title="Terminales Extraviadas"
        total={data.alertasTerminalExtraviada?.length || 0}
        rows={alertasTerminalExtraviadaFiltradas}
        emptyMessage="No hay terminales extraviadas activas para mostrar."
        searchValue={alertasTerminalExtraviadaSearch}
        onSearchChange={setAlertasTerminalExtraviadaSearch}
        placeholder="Buscar por comercio, CUIT, terminal, responsable, teléfono..."
        collapsedSet={alertaCardColapsadas}
        onToggleCollapse={toggleAlertCard}
        onActionClick={setAlertaAccionItem}
        onCopyMessage={copyToClipboard}
        currency={currency}
        percent={percent}
        PostventaBadge={PostventaBadge}
        toneForPriority={toneForPriority}
        toneForGestionState={toneForGestionState}
        toneForSegment={toneForSegment}
        toneForPattern={toneForPattern}
      />

      <InactiveTerminalsSection
        darkMode={darkMode}
        cargas={cargasInactivos}
        errores={erroresInactivos}
        inactivos={inactivos}
        rows={inactivosFiltrados}
        total={inactivos?.filas?.length || 0}
        searchValue={inactivosSearchDraft}
        onSearchChange={setInactivosSearchDraft}
        filtros={inactivosFiltros}
        onFiltrosChange={(key, value) => setInactivosFiltros((prev) => ({ ...prev, [key]: value }))}
        provinciaOptions={inactivosProvinciaOptions}
        onRefresh={aplicarFiltrosInactivos}
        collapsedSet={inactivoCardColapsadas}
        onToggleCollapse={toggleInactivoCard}
        onViewClient={(item) => ejecutarAccionAlerta("verCliente", item)}
        onManage={abrirGestionInactiva}
        currency={currency}
      />

      {activeFilters ? (
        <div className="crm-alert-floating-actions">
          <Button variant="outline-secondary" size="sm" onClick={resetAlertasFilters}>
            <FontAwesomeIcon className="me-2" icon={faFilterCircleXmark} />
            Limpiar filtros
          </Button>
        </div>
      ) : null}
    </div>
  );
}
